import type { SiteSummaryEntry } from "#/types/report";
import { formatCurrency } from "#/utils/formatCurrency";
import { EmptyState } from "#/components/ui/EmptyState";
import { Table } from "@mantine/core";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import ReportLink from "./ReportLink";
import ReportSection from "./ReportSection";

interface SiteSummaryTableProps {
  siteSummary: SiteSummaryEntry[];
  tooltip?: ReactNode;
}

const SiteSummaryTable = ({ siteSummary, tooltip }: SiteSummaryTableProps) => {
  const { t } = useTranslation();

  return (
    <ReportSection title={t("reports.inventoryScan.siteSummary.title")} tooltip={tooltip}>
      {siteSummary.length === 0 ? (
        <div className="flex h-48 items-center justify-center">
          <EmptyState title={t("common.noData")} />
        </div>
      ) : (
        <Table
          striped
          highlightOnHover
          withTableBorder
          layout="fixed"
          classNames={{
            // Nine columns in a half-width card: share the width evenly and let
            // long site names and headers wrap instead of widening the table.
            th: "whitespace-normal break-words align-top",
            td: "whitespace-normal break-words align-top",
          }}
        >
          <Table.Thead>
            <Table.Tr>
              <Table.Th className="w-[22%]">{t("reports.inventoryScan.siteSummary.site")}</Table.Th>
              <Table.Th className="text-right">
                {t("reports.inventoryScan.siteSummary.items")}
              </Table.Th>
              <Table.Th className="text-right">
                {t("reports.inventoryScan.siteSummary.estValue")}
              </Table.Th>
              <Table.Th className="text-right">
                {t("reports.inventoryScan.siteSummary.overdue0to7")}
              </Table.Th>
              <Table.Th className="text-right">
                {t("reports.inventoryScan.siteSummary.overdue8to30")}
              </Table.Th>
              <Table.Th className="text-right">
                {t("reports.inventoryScan.siteSummary.overdue31to90")}
              </Table.Th>
              <Table.Th className="text-right">
                {t("reports.inventoryScan.siteSummary.overdue90plus")}
              </Table.Th>
              <Table.Th className="text-right">
                {t("reports.inventoryScan.siteSummary.neverScanned")}
              </Table.Th>
              <Table.Th className="text-right">
                {t("reports.inventoryScan.siteSummary.percentOfTotal")}
              </Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {siteSummary.map((site) => (
              <Table.Tr key={site.siteId ?? "unknown"}>
                <Table.Td className="font-medium">
                  {site.siteId ? (
                    <ReportLink href={`/sites/${site.siteId}`}>{site.siteName}</ReportLink>
                  ) : (
                    site.siteName
                  )}
                </Table.Td>
                <Table.Td className="text-right">{site.itemCount.toLocaleString()}</Table.Td>
                <Table.Td className="text-right">{formatCurrency(site.estimatedValue)}</Table.Td>
                <Table.Td className="text-right">
                  <span className={site.overdue0to7 > 0 ? "font-semibold text-green-600" : ""}>
                    {site.overdue0to7}
                  </span>
                </Table.Td>
                <Table.Td className="text-right">
                  <span className={site.overdue8to30 > 0 ? "font-semibold text-amber-600" : ""}>
                    {site.overdue8to30}
                  </span>
                </Table.Td>
                <Table.Td className="text-right">
                  <span className={site.overdue31to90 > 0 ? "font-semibold text-red-600" : ""}>
                    {site.overdue31to90}
                  </span>
                </Table.Td>
                <Table.Td className="text-right">
                  <span className={site.overdue90plus > 0 ? "font-semibold text-red-700" : ""}>
                    {site.overdue90plus}
                  </span>
                </Table.Td>
                <Table.Td className="text-right">
                  <span className={site.neverScanned > 0 ? "font-semibold text-amber-600" : ""}>
                    {site.neverScanned}
                  </span>
                </Table.Td>
                <Table.Td className="text-right">{site.percentageOfTotal}%</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
    </ReportSection>
  );
};

export default SiteSummaryTable;
